import type { UserInfo } from '../services/api';

interface SettingsProps {
  user: UserInfo;
}

export function Settings({ user }: SettingsProps) {
  const subscription = user.subscription;
  const hasAccess = subscription && ['active', 'trialing'].includes(subscription.status);

  return (
    <>
      <div className="page-header">
        <h1>Settings</h1>
        <p>Account, subscription, and system configuration</p>
      </div>

      <div className="page-body">
        <div className="grid-2">
          {/* Account */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Account</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div className="text-sm text-muted">Name</div>
                <div>{user.user?.name || '—'}</div>
              </div>
              <div>
                <div className="text-sm text-muted">Email</div>
                <div>{user.user?.email || '—'}</div>
              </div>
              <div>
                <div className="text-sm text-muted">Role</div>
                <div>
                  <span className="badge badge-info">{user.user?.role}</span>
                </div>
              </div>
              <div>
                <div className="text-sm text-muted">Organization</div>
                <div className="font-mono text-sm">{user.user?.organizationId?.substring(0, 8)}...</div>
              </div>
            </div>
          </div>

          {/* Subscription */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Subscription</div>
            </div>
            {subscription ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <div className="text-sm text-muted">Status</div>
                  <div>
                    <span className={`badge ${subscription.status === 'active' ? 'badge-pass' :
                      subscription.status === 'trialing' ? 'badge-info' :
                        subscription.status === 'past_due' ? 'badge-warning' :
                          subscription.status === 'canceled' ? 'badge-fail' : 'badge-neutral'
                      }`}>
                      {subscription.status}
                    </span>
                  </div>
                </div>
                {subscription.currentPeriodEnd && (
                  <div>
                    <div className="text-sm text-muted">Current Period Ends</div>
                    <div>{new Date(subscription.currentPeriodEnd).toLocaleDateString()}</div>
                  </div>
                )}
                {subscription.trialEnd && (
                  <div>
                    <div className="text-sm text-muted">Trial Ends</div>
                    <div>{new Date(subscription.trialEnd).toLocaleDateString()}</div>
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <a href="/billing" className="btn btn-secondary" style={{ width: '100%', textAlign: 'center', justifyContent: 'center' }}>
                    Manage Billing & Subscription &rarr;
                  </a>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
                  No active subscription. Subscribe to unlock full access to SealVision analytics.
                </p>
                <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                  <a
                    href="https://sensifai.com/marketplace/spi-yiujt6mkjrlfsgo7aanak"
                    className="btn btn-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textAlign: 'center', justifyContent: 'center' }}
                  >
                    View Plans on Marketplace
                  </a>
                  <a href="/billing" className="btn btn-secondary" style={{ textAlign: 'center', justifyContent: 'center' }}>
                    Go to Billing Page
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* System Info */}
        <div className="card mt-6">
          <div className="card-header">
            <div className="card-title">System Information</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <div>
              <div className="text-sm text-muted">Platform Version</div>
              <div className="font-mono text-sm">1.0.0</div>
            </div>
            <div>
              <div className="text-sm text-muted">Edge Protocol</div>
              <div className="font-mono text-sm">HTTPS REST + Device Key</div>
            </div>
            <div>
              <div className="text-sm text-muted">Data Retention</div>
              <div className="font-mono text-sm">30 days (images), Unlimited (metadata)</div>
            </div>
            <div>
              <div className="text-sm text-muted">Deployment</div>
              <div className="font-mono text-sm">Coolify (Docker)</div>
            </div>
          </div>
        </div>

        {/* API Documentation */}
        <div className="card mt-6">
          <div className="card-header">
            <div className="card-title">Edge Device API</div>
            <div className="card-subtitle">Configure your SealVision edge appliance to report data</div>
          </div>
          <div style={{
            background: 'var(--bg-primary)',
            borderRadius: 'var(--radius-md)',
            padding: 16,
            fontFamily: 'var(--font-mono)',
            fontSize: '0.8rem',
            lineHeight: 1.7,
            overflow: 'auto',
          }}>
            <div style={{ color: 'var(--text-muted)' }}># Report an inspection result</div>
            <div>POST /api/inspections/report</div>
            <div>Headers: X-Device-Key: {'<'}your-device-key{'>'}</div>
            <div>Content-Type: multipart/form-data</div>
            <div>&nbsp;</div>
            <div style={{ color: 'var(--text-muted)' }}># Fields:</div>
            <div>result: pass | fail | warning</div>
            <div>confidenceScore: 0.0 - 1.0</div>
            <div>defectType: contamination | wrinkle | under_heating | ...</div>
            <div>batchCode: BATCH-2026-001</div>
            <div>thermalImage: @thermal_frame.jpg</div>
            <div>rgbImage: @rgb_frame.jpg</div>
            <div>&nbsp;</div>
            <div style={{ color: 'var(--text-muted)' }}># Device heartbeat (every 30s)</div>
            <div>POST /api/inspections/heartbeat</div>
            <div>Headers: X-Device-Key: {'<'}your-device-key{'>'}</div>
          </div>
        </div>

        {/* Actions */}
        <div className="card mt-6">
          <div className="card-header">
            <div className="card-title">Actions</div>
          </div>
          <div className="flex gap-3">
            <a href="/auth/logout" className="btn btn-secondary">Sign Out</a>
          </div>
        </div>
      </div>
    </>
  );
}
